import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import colors from "../../app/colors";
import { useUsers } from "../providers/users-provider";
import { useVideoCallProvider } from "../providers/video-call-provider";
import DECLINE_CALL_MUTATION from "../providers/video-call-provider/networking/decline-call-mutation";
import type { IWebSocketPayloadIncomingCall } from "../providers/websocket-provider/types";
import UserAvatar from "../_shared/user-avatar";
import useIncomingCallRing from "modules/hooks/use-incoming-call-ring";
import { useOverlay } from "modules/providers/overlay-provider";

/** How long the modal rings before dismissing itself when unanswered. */
const UNANSWERED_TIMEOUT_MILLISECONDS = 45_000;

interface IProps {
  call: IWebSocketPayloadIncomingCall;
}

const IncomingCallModal: React.FC<IProps> = ({ call }) => {
  const { closeModal } = useOverlay();
  const { fullScreenVideoCall } = useVideoCallProvider();
  const { usersById } = useUsers();
  const [declineCallMutation] = useMutation(DECLINE_CALL_MUTATION);

  useIncomingCallRing();

  // Auto-dismiss when unanswered. The timeout is armed once on mount;
  // closeModal is kept in a ref because its identity changes with the modal
  // stack.
  const closeModalRef = useRef(closeModal);
  useEffect(() => {
    closeModalRef.current = closeModal;
  }, [closeModal]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      closeModalRef.current();
    }, UNANSWERED_TIMEOUT_MILLISECONDS);
    return () => clearTimeout(timeout);
  }, []);

  const joinCall = useCallback(() => {
    fullScreenVideoCall({
      callId: call.callId,
      presentationUserId: call.fromUserId,
    });
    closeModal();
  }, [closeModal, fullScreenVideoCall, call]);

  const ignoreCall = useCallback(() => {
    // Fire-and-forget: the ring should stop immediately either way.
    declineCallMutation({ variables: { id: call.callId } }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Error declining call", error);
    });
    closeModal();
  }, [call.callId, closeModal, declineCallMutation]);

  const callingUserFirstName = useMemo(() => {
    if (!call) return "";
    return usersById[call.fromUserId]?.firstName;
  }, [call, usersById]);

  const callWithUsersFirstNames = useMemo(() => {
    if (!call) return "";
    const usersFirstName = call.accessUserIdList.map(
      (id) => usersById[id]?.firstName,
    );
    if (usersFirstName.length === 0) return "";
    if (usersFirstName.length === 1) return usersFirstName[0];
    if (usersFirstName.length === 2) {
      return `${usersFirstName[0]} & ${usersFirstName[1]}`;
    }
    return `${usersFirstName.slice(0, -1).join(", ")} & ${
      usersFirstName[usersFirstName.length - 1]
    }`;
  }, [call, usersById]);

  return (
    <Modal isOpen onClose={closeModal} size="sm" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16} p="32px">
        <ModalBody p={0}>
          {call ? (
            <VStack w="full" gap={0} mt="10px">
              <Box p="5px" borderRadius="100%" bgColor="#E7E9ED">
                <Box p="5px" borderRadius="100%" bgColor="#D0D3DA">
                  <Box p="5px" borderRadius="100%" bgColor="#B8BCC8">
                    <UserAvatar
                      userId={call.fromUserId}
                      size="md"
                      displayOnlineIndicator={false}
                    />
                  </Box>
                </Box>
              </Box>
              <Text
                fontFamily="Outfit"
                fontSize={24}
                fontWeight={600}
                color={colors.blackSecondary}
                py="16px"
              >
                Incoming {call.isVideo ? "Video" : "Audio"} Call...
              </Text>
              <Text
                fontSize={14}
                fontWeight={500}
                color={colors.gray50}
                pb="16px"
                textAlign="center"
              >
                {callingUserFirstName} is inviting you to a{" "}
                {call.isVideo ? "video" : "audio"} call with{" "}
                {callWithUsersFirstNames}
              </Text>
              <Flex w="full" gap="18px">
                <Button
                  flex={1}
                  onClick={ignoreCall}
                  fontSize={16}
                  fontWeight={500}
                  h="50px"
                  backgroundColor={colors.red}
                  _hover={{
                    bgColor: colors.red,
                    opacity: 0.8,
                  }}
                  _active={{
                    bgColor: colors.red,
                  }}
                >
                  Ignore
                </Button>
                <Button
                  flex={1}
                  onClick={joinCall}
                  fontSize={16}
                  fontWeight={500}
                  h="50px"
                  backgroundColor={colors.greenPrimary}
                  _hover={{
                    bgColor: colors.greenPrimary,
                    opacity: 0.8,
                  }}
                  _active={{
                    bgColor: colors.greenPrimary,
                  }}
                >
                  Join
                </Button>
              </Flex>
            </VStack>
          ) : (
            <Center>
              <Spinner />
            </Center>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(IncomingCallModal);
